import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, type LucideIcon } from 'lucide-react';
import { useCountUp } from '../../hooks/useCountUp';

interface StatCardProps {
  title: string;
  /** Numeric target that drives the count-up animation. */
  value: number;
  /** Formats the animated number for display (e.g. thousands separators, currency). */
  formatValue?: (value: number) => string;
  icon: LucideIcon;
  /** Gradient (or solid) Tailwind classes for the icon chip. */
  color: string;
  /** Optional trend / sub-label node rendered under the value. */
  change?: React.ReactNode;
  /** When set, the whole card becomes a router Link to this route. */
  to?: string;
  /** Index in the grid — drives the staggered enter animation. */
  index?: number;
}

const defaultFormat = (value: number) => Math.round(value).toLocaleString();

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  formatValue = defaultFormat,
  icon: Icon,
  color,
  change,
  to,
  index = 0,
}) => {
  const animated = useCountUp(value);

  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          <p className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">
            {formatValue(animated)}
          </p>
        </div>
        <div
          className={`shrink-0 grid place-items-center w-11 h-11 rounded-xl shadow-sm ${color} transition-transform duration-300 group-hover:scale-105 group-hover:-rotate-3`}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {change && <div className="mt-3">{change}</div>}
      {to && (
        <ArrowUpRight
          className="absolute bottom-4 right-4 w-4 h-4 text-gray-300 opacity-0 translate-x-1 translate-y-1 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0"
          aria-hidden
        />
      )}
    </>
  );

  // Staggered fade-in-up on mount; ~80ms between cards.
  const enter = 'motion-safe:animate-fade-in-up';
  const enterStyle = { animationDelay: `${index * 80}ms` };

  const base =
    'group relative bg-white p-5 rounded-2xl shadow-sm ring-1 ring-gray-900/[0.04]';

  if (to) {
    return (
      <Link
        to={to}
        style={enterStyle}
        className={`${base} block transition-[box-shadow,transform] duration-300 hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/50 focus-visible:ring-offset-2 ${enter}`}
      >
        {content}
      </Link>
    );
  }

  return (
    <div style={enterStyle} className={`${base} ${enter}`}>
      {content}
    </div>
  );
};

export default StatCard;
